import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Typography,
} from "@mui/material";

import {
    CheckCircleOutline as CheckCircleOutlineIcon,
    ExpandLess,
    ExpandMore,
    FilterList as FilterListIcon,
    Info as InfoIcon,
    Save as SaveIcon,
} from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";

import useFilteredStudents from "../hooks/useFilteredStudents";
import useSubjectsAndUnits from "../hooks/useSubjectsAndUnits";
import useProgressCardHandlers from "../hooks/useProgressCardHandlers";

import {
    FloatingGradelyLoader,
} from "../loadingIcons/LoadingIcons";


const PROFICIENCY_OPTIONS = [
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];


const DOK_STYLES = {
    1: {
        color: "#2e7d32",
        background: "#f1f8e9",
        border: "#a5d6a7",
    },
    2: {
        color: "#1565c0",
        background: "#e3f2fd",
        border: "#90caf9",
    },
    3: {
        color: "#6a1b9a",
        background: "#f3e5f5",
        border: "#ce93d8",
    },
    4: {
        color: "#795548",
        background: "#efebe9",
        border: "#bcaaa4",
    },
};


const UnitLevelProgress = () => {
    const theme = useTheme();

    // =========================================================
    // FILTERS
    // =========================================================

    const [sectionLevel, setSectionLevel] = useState("");
    const [classLevel, setClassLevel] = useState("");
    const [academicYear, setAcademicYear] = useState("");
    const [filtersExpanded, setFiltersExpanded] = useState(false);


    // =========================================================
    // ASSESSMENT SELECTION
    // =========================================================

    const [termNumber, setTermNumber] = useState("");
    const [selectedStudent, setSelectedStudent] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedUnit, setSelectedUnit] = useState("");
    const [overallAssessment, setOverallAssessment] = useState("");
    const [educatorComment, setEducatorComment] = useState("");


    // =========================================================
    // PROGRESS CARDs
    // =========================================================

    const [reportCards, setReportCards] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);


    // =========================================================
    // DIALOGS
    // =========================================================

    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [showMissingSelectionDialog, setShowMissingSelectionDialog] =
        useState(false);


    // =========================================================
    // DATA HOOKS
    // =========================================================

    const students = useFilteredStudents(
        classLevel,
        sectionLevel,
        academicYear
    );

    const {
        subjects,
        units,
    } = useSubjectsAndUnits(selectedSubject);


    // =========================================================
    // PROGRESS CARD HOOK
    // =========================================================

    const {
        handleRetrieveReportCard,
        handleCreateProgressCard,
        handleUpdateProgressCard,
        SnackbarComponent,
    } = useProgressCardHandlers({
        selectedStudent,
        selectedSubject,
        selectedUnit,
        selectedTerm: termNumber,
        setReportCards,
        setIsDirty,
        setOverallAssessment,
        setEducatorComment,
    });


    // =========================================================
    // RETRIEVE REPORT
    // =========================================================

    useEffect(() => {
        const hasAllSelections =
            String(selectedStudent).length > 0 &&
            String(selectedSubject).length > 0 &&
            String(selectedUnit).length > 0 &&
            [1, 2, 3].includes(parseInt(termNumber, 10));

        if (hasAllSelections) {
            handleRetrieveReportCard();
        }
    }, [
        selectedStudent,
        selectedSubject,
        selectedUnit,
        termNumber,
    ]);


    // =========================================================
    // BASIC HELPERS
    // =========================================================

    const markDirty = () => {
        setIsDirty(true);
    };

    const clearCurrentAssessment = () => {
        setReportCards(null);
        setOverallAssessment("");
        setEducatorComment("");
    };


    // =========================================================
    // CHANGE STUDENT / SUBJECT / UNIT
    // =========================================================

    const handleSelectionChange = (type, value) => {
        if (isDirty) {
            setPendingSelection({
                type,
                value,
            });
            setShowDiscardDialog(true);
            return;
        }

        applySelection(type, value);
    };


    const applySelection = (type, value) => {
        clearCurrentAssessment();

        if (type === "student") {
            setSelectedStudent(value);
        } else if (type === "subject") {
            setSelectedSubject(value);
            setSelectedUnit("");
        } else if (type === "unit") {
            setSelectedUnit(value);
        }
    };


    const confirmDiscard = () => {
        if (pendingSelection) {
            const {
                type,
                value,
            } = pendingSelection;

            setIsDirty(false);
            applySelection(type, value);
        }

        setPendingSelection(null);
        setShowDiscardDialog(false);
    };


    // =========================================================
    // PREVIOUS / NEXT STUDENT
    // =========================================================

    const handleNextStudent = () => {
        if (!selectedSubject || !selectedUnit) {
            setShowMissingSelectionDialog(true);
            return;
        }

        const currentIndex = students.findIndex(
            (student) => String(student.id) === String(selectedStudent)
        );

        if (
            currentIndex !== -1 &&
            currentIndex < students.length - 1
        ) {
            const nextStudent = students[currentIndex + 1];

            handleSelectionChange(
                "student",
                nextStudent.id
            );
        }
    };


    const handlePrevStudent = () => {
        if (!selectedSubject || !selectedUnit) {
            setShowMissingSelectionDialog(true);
            return;
        }

        const currentIndex = students.findIndex(
            (student) => String(student.id) === String(selectedStudent)
        );

        if (currentIndex > 0) {
            const previousStudent = students[currentIndex - 1];

            handleSelectionChange(
                "student",
                previousStudent.id
            );
        }
    };


    // =========================================================
    // DISPLAY CONTEXT
    // =========================================================

    const selectedStudentObject = students.find(
        (student) => String(student.id) === String(selectedStudent)
    );

    const selectedSubjectObject = subjects.find(
        (subject) => String(subject.id) === String(selectedSubject)
    );

    const selectedUnitObject = units.find(
        (unit) => String(unit.id) === String(selectedUnit)
    );

    const studentDisplayName = selectedStudentObject
        ? `${selectedStudentObject.first_name || ""} ${selectedStudentObject.last_name || ""}`.trim()
        : reportCards?.student_full_name || "Student";

    const subjectDisplayName = selectedSubjectObject
        ? `${selectedSubjectObject.subject_name || ""} ${selectedSubjectObject.class_level || ""}`.trim()
        : "Subject";

    const unitDisplayName = selectedUnitObject?.unit_name || "Unit";


    // =========================================================
    // GROUP ENTRIES BY DOK
    // =========================================================

    const entriesByDok = useMemo(() => {
        const grouped = {
            1: [],
            2: [],
            3: [],
            4: [],
        };

        const entries = reportCards?.entries || [];

        entries.forEach((entry, index) => {
            const level = Number(
                entry?.dok_level?.level
            );

            if (grouped[level]) {
                grouped[level].push({
                    entry,
                    index,
                });
            }
        });

        return grouped;
    }, [reportCards]);


    // =========================================================
    // COUNTS / STATUS / VALIDATION
    // =========================================================

    const totalSkills = reportCards?.entries?.length || 0;

    const totalAchieved = reportCards?.entries?.filter(
        (entry) => Boolean(entry.is_achieved)
    ).length || 0;

    // Assessed means at least one Skill is currently achieved.
    // Not Assessed means none are — this is computed automatically
    // from the Skill entries, matching the backend, and is no
    // longer a manual teacher toggle.
    const assessmentStatus =
        totalAchieved > 0 ? "assessed" : "not_assessed";


    const getDokAchievement = (level) => {
        const skills = entriesByDok[level] || [];

        const achieved = skills.filter(
            ({ entry }) => Boolean(entry.is_achieved)
        ).length;

        return {
            total: skills.length,
            achieved,
        };
    };


    const getDokProficiency = (level) => {
        const item = (
            reportCards?.dok_assessments || []
        ).find((assessment) => {
            const itemLevel = Number(
                assessment?.dok_level?.level ??
                assessment?.dok_level
            );

            return itemLevel === Number(level);
        });

        return item?.proficiency || "";
    };


    const selectedDokLevels = [1, 2, 3, 4].filter(
        (level) => getDokAchievement(level).achieved > 0
    );


    const hasMissingDokProficiency =
        selectedDokLevels.some(
            (level) => !getDokProficiency(level)
        );


    // =========================================================
    // CHANGE INDIVIDUAL SKILL
    // =========================================================

    const handleSkillAchievedChange = (
        originalIndex,
        checked
    ) => {
        setReportCards((current) => {
            if (!current?.entries) {
                return current;
            }

            const targetEntry =
                current.entries[originalIndex];

            if (!targetEntry) {
                return current;
            }

            const targetDokLevel = Number(
                targetEntry?.dok_level?.level
            );

            const updatedEntries = current.entries.map(
                (entry, index) =>
                    index === originalIndex
                        ? {
                            ...entry,
                            is_achieved: checked,
                        }
                        : entry
            );

            let updatedDokAssessments =
                current.dok_assessments || [];

            // If the last checked skill in a DOK is unchecked,
            // clear that DOK's proficiency so stale ratings are not saved.
            if (!checked) {
                const stillHasAchievedSkill =
                    updatedEntries.some(
                        (entry) =>
                            Number(
                                entry?.dok_level?.level
                            ) === targetDokLevel &&
                            Boolean(entry.is_achieved)
                    );

                if (!stillHasAchievedSkill) {
                    updatedDokAssessments =
                        updatedDokAssessments.map(
                            (assessment) => {
                                const level = Number(
                                    assessment?.dok_level?.level ??
                                    assessment?.dok_level
                                );

                                return level === targetDokLevel
                                    ? {
                                        ...assessment,
                                        proficiency: "",
                                    }
                                    : assessment;
                            }
                        );
                }
            }

            return {
                ...current,
                entries: updatedEntries,
                dok_assessments:
                    updatedDokAssessments,
            };
        });

        markDirty();
    };


    // =========================================================
    // CHANGE DOK PROFICIENCY
    // =========================================================

    const handleDokProficiencyChange = (
        level,
        proficiency
    ) => {
        setReportCards((current) => {
            if (!current) {
                return current;
            }

            const existing =
                current.dok_assessments || [];

            let found = false;

            const updated = existing.map(
                (assessment) => {
                    const itemLevel = Number(
                        assessment?.dok_level?.level ??
                        assessment?.dok_level
                    );

                    if (itemLevel === Number(level)) {
                        found = true;

                        return {
                            ...assessment,
                            proficiency,
                        };
                    }

                    return assessment;
                }
            );

            if (!found) {
                const dokLevelObject =
                    entriesByDok[level]?.[0]                        ?.entry
                        ?.dok_level || {
                            level,
                        };

                updated.push({
                    dok_level: dokLevelObject,
                    proficiency,
                });
            }

            return {
                ...current,
                dok_assessments: updated,
            };
        });

        markDirty();
    };


    // =========================================================
    // SAVE ASSESSMENT
    // =========================================================

    const handleSave = async () => {
        if (
            !reportCards ||
            totalSkills === 0 ||
            hasMissingDokProficiency
        ) {
            return;
        }

        setSaveLoading(true);

        try {
            const actionProps = {
                reportCards,
                setReportCards,
                overall_assessment:
                    overallAssessment,
                educator_comment:
                    educatorComment,
                term_number:
                    parseInt(termNumber, 10),
            };

            if (reportCards?.id) {
                await handleUpdateProgressCard(
                    actionProps
                );
            } else {
                await handleCreateProgressCard(
                    actionProps
                );
            }
        } catch (error) {
            console.error(
                "Failed to save assessment report:",
                error
            );
        } finally {
            setSaveLoading(false);
        }
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (
        <Box>

            {/* =================================================
                STUDENT FILTERS
            ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: filtersExpanded
                        ? "2px solid #4caf50"
                        : "1px solid #e0e0e0",
                    transition: "all 0.3s ease",
                }}
            >
                <Box
                    onClick={() =>
                        setFiltersExpanded(
                            !filtersExpanded
                        )
                    }
                    sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        backgroundColor: filtersExpanded
                            ? "#f1f8e9"
                            : "transparent",
                        borderRadius: filtersExpanded
                            ? "8px 8px 0 0"
                            : "8px",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            backgroundColor: "#f5f5f5",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <FilterListIcon
                            sx={{
                                color: filtersExpanded
                                    ? "#4caf50"
                                    : "#666",
                                fontSize: 20,
                            }}
                        />

                        <Typography
                            fontWeight="bold"
                            sx={{
                                color: filtersExpanded
                                    ? "#4caf50"
                                    : "text.primary",
                                fontSize: "0.95rem",
                            }}
                        >
                            Filter Students by Class, Section & Academic Year
                        </Typography>

                        {(
                            classLevel ||
                            sectionLevel ||
                            academicYear
                        ) && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#ff5722",
                                    ml: 1,
                                }}
                            />
                        )}
                    </Box>

                    {filtersExpanded ? (
                        <ExpandLess
                            sx={{
                                color: "#4caf50",
                            }}
                        />
                    ) : (
                        <ExpandMore
                            sx={{
                                color: "#666",
                            }}
                        />
                    )}
                </Box>

                <Collapse in={filtersExpanded}>
                    <Box
                        sx={{
                            p: 2,
                            pt: 0,
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Select Grade Level
                                    </InputLabel>

                                    <Select
                                        value={classLevel}
                                        onChange={(e) => {
                                            setClassLevel(
                                                e.target.value
                                            );
                                            setSelectedStudent("");
                                            clearCurrentAssessment();
                                        }}
                                        input={
                                            <OutlinedInput
                                                label="Select Grade Level"
                                            />
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Grade Level
                                        </MenuItem>

                                        {[
                                            "KG",
                                            1,
                                            2,
                                            3,
                                            4,
                                            5,
                                            6,
                                            7,
                                            8,
                                        ].map((level) => (
                                            <MenuItem
                                                key={level}
                                                value={level}
                                            >
                                                {level}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Select Section
                                    </InputLabel>

                                    <Select
                                        value={sectionLevel}
                                        onChange={(e) => {
                                            setSectionLevel(
                                                e.target.value
                                            );
                                            setSelectedStudent("");
                                            clearCurrentAssessment();
                                        }}
                                        input={
                                            <OutlinedInput
                                                label="Select Section"
                                            />
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Section
                                        </MenuItem>

                                        {[
                                            1,
                                            2,
                                            3,
                                            4,
                                            5,
                                            6,
                                            8,
                                            9,
                                        ].map((section) => (
                                            <MenuItem
                                                key={section}
                                                value={section}
                                            >
                                                Section {section}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Select Academic Year
                                    </InputLabel>

                                    <Select
                                        value={academicYear}
                                        onChange={(e) => {
                                            setAcademicYear(
                                                e.target.value
                                            );
                                            setSelectedStudent("");
                                            clearCurrentAssessment();
                                        }}
                                        input={
                                            <OutlinedInput
                                                label="Select Academic Year"
                                            />
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Academic Year
                                        </MenuItem>

                                        {[
                                            "2025-2026",
                                            "2026-2027",
                                            "2027-2028",
                                            "2028-2029",
                                            "2029-2030",
                                        ].map((year) => (
                                            <MenuItem
                                                key={year}
                                                value={year}
                                            >
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {(
                            classLevel ||
                            sectionLevel ||
                            academicYear
                        ) && (
                            <Box
                                sx={{
                                    mt: 2,
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setClassLevel("");
                                        setSectionLevel("");
                                        setAcademicYear("");
                                        setSelectedStudent("");
                                        clearCurrentAssessment();
                                    }}
                                >
                                    Clear All Filters
                                </Button>
                            </Box>
                        )}

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={2}
                            sx={{
                                textAlign: "center",
                            }}
                        >
                            {students.length} students available
                        </Typography>
                    </Box>
                </Collapse>
            </Paper>


            {/* =================================================
                STUDENT / SUBJECT / UNIT / TERM
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 2,
                        sm: 3,
                    },
                    mb: 3,
                    borderRadius: 3,
                    border:
                        `1px solid ${theme.palette.divider}`,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                >
                    1. Select Student, Subject, Unit & Term
                </Typography>

                <Grid
                    container
                    spacing={2}
                >
                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >
                        <FormControl fullWidth>
                            <InputLabel>
                                Select Student
                            </InputLabel>

                            <Select
                                value={selectedStudent}
                                onChange={(e) =>
                                    handleSelectionChange(
                                        "student",
                                        e.target.value
                                    )
                                }
                                input={
                                    <OutlinedInput
                                        label="Select Student"
                                    />
                                }
                            >
                                <MenuItem value="">
                                    Select Student
                                </MenuItem>

                                {students.map((student) => (
                                    <MenuItem
                                        key={student.id}
                                        value={student.id}
                                    >
                                        {student.first_name}{" "}
                                        {student.last_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box
                            mt={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <Button
                                onClick={handlePrevStudent}
                                variant="outlined"
                                size="small"
                                sx={{
                                    fontSize: "0.7rem",
                                }}
                            >
                                &lt; Prev
                            </Button>

                            <Button
                                onClick={handleNextStudent}
                                variant="outlined"
                                size="small"
                                sx={{
                                    fontSize: "0.7rem",
                                }}
                            >
                                Next &gt;
                            </Button>
                        </Box>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >
                        <FormControl fullWidth>
                            <InputLabel>
                                Select Subject
                            </InputLabel>

                            <Select
                                value={selectedSubject}
                                                                onChange={(e) =>
                                    handleSelectionChange(
                                        "subject",
                                        e.target.value
                                    )
                                }
                                input={
                                    <OutlinedInput
                                        label="Select Subject"
                                    />
                                }
                            >
                                <MenuItem value="">
                                    Select Subject
                                </MenuItem>

                                {subjects.map((subject) => (
                                    <MenuItem
                                        key={subject.id}
                                        value={subject.id}
                                    >
                                        {subject.subject_name}{" "}
                                        {subject.class_level}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >
                        <FormControl fullWidth>
                            <InputLabel>
                                Select Unit
                            </InputLabel>

                            <Select
                                value={selectedUnit}
                                onChange={(e) =>
                                    handleSelectionChange(
                                        "unit",
                                        e.target.value
                                    )
                                }
                                input={
                                    <OutlinedInput
                                        label="Select Unit"
                                    />
                                }
                            >
                                <MenuItem value="">
                                    Select Unit
                                </MenuItem>

                                {units.map((unit) => (
                                    <MenuItem
                                        key={unit.id}
                                        value={unit.id}
                                    >
                                        {unit.unit_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >
                        <FormControl fullWidth>
                            <InputLabel>
                                Select Term
                            </InputLabel>

                            <Select
                                value={termNumber}
                                onChange={(e) => {
                                    if (isDirty) {
                                        setPendingSelection({
                                            type: "term",
                                            value: e.target.value,
                                        });

                                        setShowDiscardDialog(
                                            true
                                        );

                                        return;
                                    }

                                    clearCurrentAssessment();
                                    setTermNumber(
                                        e.target.value
                                    );
                                }}
                                input={
                                    <OutlinedInput
                                        label="Select Term"
                                    />
                                }
                            >
                                <MenuItem value="">
                                    Select Term
                                </MenuItem>

                                <MenuItem value={1}>
                                    Term 1
                                </MenuItem>

                                <MenuItem value={2}>
                                    Term 2
                                </MenuItem>

                                <MenuItem value={3}>
                                    Term 3
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>


            {/* =================================================
                ASSESSMENT
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 2,
                        sm: 3,
                    },
                    mb: 3,
                    borderRadius: 3,
                    border:
                        `1px solid ${theme.palette.divider}`,
                    backgroundColor: "#ffffff",
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={2}
                    mb={2}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                        >
                            2. Enter Achieved DOK & Overall Assessment
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                maxWidth: 760,
                            }}
                        >
                            Check each skill the student demonstrated.
                            Then assign an overall proficiency for each
                            DOK level with selected skills.
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                    >
                        <Chip
                            label={
                                isDirty
                                    ? "Unsaved Changes"
                                    : "Saved"
                            }
                            color={
                                isDirty
                                    ? "warning"
                                    : "success"
                            }
                            variant="outlined"
                        />

                        <Button
                            variant="contained"
                            startIcon={
                                <SaveIcon />
                            }
                            disabled={
                                saveLoading ||
                                totalSkills === 0 ||
                                hasMissingDokProficiency
                            }
                            onClick={handleSave}
                        >
                            {saveLoading
                                ? "Saving..."
                                : reportCards?.id
                                    ? "Update Assessment"
                                    : "Save Assessment"}
                        </Button>
                    </Box>
                </Box>


                {/* Context summary */}

                {selectedStudent &&
                    selectedSubject &&
                    selectedUnit && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor:
                                    "#fafafa",
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                            >
                                {studentDisplayName}
                                {" · "}
                                {subjectDisplayName}
                                {" · "}
                                {unitDisplayName}
                                {termNumber
                                    ? ` · Term ${termNumber}`
                                    : ""}
                            </Typography>
                        </Paper>
                    )}


                {/* Instructions */}

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor:
                            "#f8f9f5",
                    }}
                >
                    <Box
                        display="flex"
                        gap={1}
                        alignItems="flex-start"
                    >
                        <InfoIcon
                            sx={{
                                color: "#57643F",
                                mt: 0.25,
                            }}
                        />

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Check all skills observed in this session.
                            Unchecked skills were not demonstrated or
                            were not assessed. A DOK proficiency is
                            required when one or more skills are
                            selected for that DOK level.
                        </Typography>
                    </Box>
                </Paper>


                {/* Assessment status */}

                {reportCards && (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 2,
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Student
                                </Typography>

                                <Typography
                                    fontWeight="bold"
                                >
                                    {studentDisplayName}
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={8}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Assessment Status
                                </Typography>

                                <Box
                                    mt={0.75}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                >
                                    <Chip
                                        size="small"
                                        label={
                                            assessmentStatus ===
                                            "assessed"
                                                ? "Assessed"
                                                : "Not Assessed"
                                        }
                                        sx={{
                                            fontWeight: 700,
                                            color:
                                                assessmentStatus ===
                                                "assessed"
                                                    ? "#2E6B2E"
                                                    : "#6B6F5E",
                                            backgroundColor:
                                                assessmentStatus ===
                                                "assessed"
                                                    ? "#E8F3E1"
                                                    : "#EFEFE8",
                                        }}
                                    />

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Set automatically once at
                                        least one skill is checked
                                        below.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}


                {/* Overall assessment */}

                <Box
                    sx={{
                        mb: 3,
                        maxWidth: 420,
                    }}
                >
                    <FormControl
                        fullWidth
                    >
                        <InputLabel>
                            Overall Proficiency
                        </InputLabel>

                        <Select
                            value={overallAssessment}
                            onChange={(e) => {
                                setOverallAssessment(
                                    e.target.value
                                );

                                markDirty();
                            }}
                            input={
                                <OutlinedInput
                                    label="Overall Proficiency"
                                />
                            }
                        >
                            <MenuItem value="">
                                Select Assessment
                            </MenuItem>

                            {PROFICIENCY_OPTIONS.map(
                                (level) => (
                                    <MenuItem
                                        key={level}
                                        value={level}
                                    >
                                        {level}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>


                {/* No skills */}

                {selectedUnit &&
                    totalSkills === 0 && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                textAlign: "center",
                                borderStyle: "dashed",
                            }}
                        >
                            <Typography
                                fontWeight="bold"
                            >
                                No skills are available for this unit.
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                }}
                            >
                                Add skills in Manage Skills before
                                creating an assessment.
                            </Typography>
                        </Paper>
                    )}


                {/* DOK sections */}

                {[1, 2, 3, 4].map((level) => {
                    const skills =
                        entriesByDok[level] || [];

                    if (skills.length === 0) {
                        return null;
                    }

                    const {
                        achieved,
                        total,
                    } = getDokAchievement(level);

                    const dokDescription =
                        skills[0]
                            ?.entry
                            ?.dok_level
                            ?.description || "";

                    const proficiency =
                        getDokProficiency(level);

                    const proficiencyRequired =
                        achieved > 0 &&
                        !proficiency;

                    const dokStyle =
                        DOK_STYLES[level];

                    return (
                        <Paper
                            key={level}
                            variant="outlined"
                            sx={{
                                mb: 2.5,
                                borderRadius: 3,
                                overflow: "hidden",
                                borderColor:
                                    dokStyle.border,
                            }}
                        >
                            {/* DOK header */}

                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    backgroundColor:
                                        dokStyle.background,
                                    borderBottom:
                                        `1px solid ${dokStyle.border}`,
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        flexWrap="wrap"
                                    >
                                        <Chip
                                            size="small"
                                            label={`DOK ${level}`}
                                            sx={{
                                                fontWeight: "bold",
                                                color:
                                                    dokStyle.color,
                                            }}
                                        />

                                        <Typography
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    dokStyle.color,
                                            }}
                                        >
                                            {dokDescription}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                dokStyle.color,
                                        }}
                                    >
                                        {achieved} of {total} skills selected
                                    </Typography>
                                </Box>
                            </Box>


                            {/* DOK body */}

                            <Grid
                                container
                                spacing={0}
                            >
                                {/* Skills */}

                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                    sx={{
                                        borderRight: {
                                            xs: "none",
                                            md:
                                                `1px solid ${theme.palette.divider}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderBottom:
                                                `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                        >
                                            Skills Observed
                                        </Typography>
                                    </Box>

                                    {skills.map(
                                        ({
                                            entry,
                                            index,
                                        }) => (
                                            <Box
                                                key={
                                                    entry.skill ??
                                                    entry.id ??
                                                    index
                                                }
                                                sx={{
                                                    px: 2,
                                                    py: 1.25,
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "flex-start",
                                                    gap: 1,
                                                    borderBottom:
                                                        `1px solid ${theme.palette.divider}`,
                                                    "&:last-child":
                                                        {
                                                            borderBottom:
                                                                "none",
                                                        },
                                                    backgroundColor:
                                                        entry.is_achieved
                                                            ? dokStyle.background
                                                            : "#fafafa",
                                                    opacity:
                                                        entry.is_achieved
                                                            ? 1
                                                            : 0.72,
                                                }}
                                            >
                                                <Checkbox
                                                    checked={
                                                        Boolean(
                                                            entry.is_achieved
                                                        )
                                                    }
                                                    onChange={(
                                                        e
                                                    ) =>
                                                        handleSkillAchievedChange(
                                                            index,
                                                            e.target.checked
                                                        )
                                                    }
                                                    sx={{
                                                        mt: -0.5,
                                                    }}
                                                />

                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        pt: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            lineHeight: 1.55,
                                                            fontWeight:
                                                                entry.is_achieved
                                                                    ? 600
                                                                    : 400,
                                                        }}
                                                    >
                                                        {
                                                            entry.skill_description
                                                        }
                                                    </Typography>

                                                    {entry.is_achieved && (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.5}
                                                            mt={0.75}
                                                        >
                                                            <CheckCircleOutlineIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color:
                                                                        dokStyle.color,
                                                                }}
                                                            />

                                                            <Typography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    color:
                                                                        dokStyle.color,
                                                                }}
                                                            >
                                                                Demonstrated
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        )
                                    )}
                                </Grid>


                                {/* Proficiency */}

                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                >
                                    <Box
                                        sx={{
                                            p: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            mb={1}
                                        >
                                            Overall Proficiency
                                        </Typography>

                                        <FormControl
                                            fullWidth
                                            size="small"
                                            error={
                                                proficiencyRequired
                                            }
                                            disabled={
                                                achieved === 0
                                            }
                                        >
                                            <InputLabel>
                                                Proficiency
                                            </InputLabel>

                                            <Select
                                                value={
                                                    proficiency
                                                }
                                                onChange={(e) =>
                                                    handleDokProficiencyChange(
                                                        level,
                                                        e.target.value
                                                    )
                                                }
                                                input={
                                                    <OutlinedInput
                                                        label="Proficiency"
                                                    />
                                                }
                                            >
                                                <MenuItem value="">
                                                    Select
                                                </MenuItem>

                                                {PROFICIENCY_OPTIONS.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={
                                                                option
                                                            }
                                                            value={
                                                                option
                                                            }
                                                        >
                                                            {
                                                                option
                                                            }
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>

                                        {achieved === 0 && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display:
                                                        "block",
                                                    mt: 1,
                                                }}
                                            >
                                                Select at least one
                                                skill to assign a
                                                proficiency.
                                            </Typography>
                                        )}

                                        {proficiencyRequired && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                fontWeight="bold"
                                                sx={{
                                                    display:
                                                        "block",
                                                    mt: 1,
                                                }}
                                            >
                                                Required to save.
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}


                {/* Summary */}

                {totalSkills > 0 && (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mt: 3,
                            borderRadius: 2,
                            backgroundColor:
                                "#fafafa",
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={2}
                        >
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                            >
                                {studentDisplayName}
                                {" · "}
                                {subjectDisplayName}
                                {" · "}
                                {unitDisplayName}
                                {" · "}
                                {totalAchieved} skills assessed
                                {selectedDokLevels.length >
                                0
                                    ? ` · DOK ${selectedDokLevels.join(
                                        " & "
                                    )}`
                                    : ""}
                            </Typography>

                            <Button
                                variant="contained"
                                startIcon={
                                    <SaveIcon />
                                }
                                disabled={
                                    saveLoading ||
                                    totalSkills === 0 ||
                                    hasMissingDokProficiency
                                }
                                onClick={handleSave}
                            >
                                {saveLoading
                                    ? "Saving..."
                                    : reportCards?.id
                                        ? "Update Assessment"
                                        : "Save Assessment"}
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Paper>


            {/* =================================================
                EDUCATOR COMMENT
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 2,
                        sm: 3,
                    },
                    mb: 3,
                    borderRadius: 3,
                    border:
                        `1px solid ${theme.palette.divider}`,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                >
                    3. Provide a Comment on Student Performance
                </Typography>

                <Chip
                    icon={
                        <InfoIcon />
                    }
                    label="Write a brief comment on the student's performance. This will appear in the report card."
                    variant="outlined"
                    sx={{
                        backgroundColor:
                            "#f8f9f5",
                        borderColor:
                            "#57643F",
                        color:
                            "#57643F",
                        mb: 2,
                        "& .MuiChip-icon": {
                            color:
                                "#57643F",
                        },
                    }}
                />

                <FormControl fullWidth>
                    <OutlinedInput
                        multiline
                        minRows={3}
                        maxRows={6}
                        placeholder="Write your comment here..."
                        value={educatorComment}
                        onChange={(e) => {
                            setEducatorComment(
                                e.target.value
                            );

                            markDirty();
                        }}
                    />
                </FormControl>
            </Paper>
                        {/* =================================================
                UNSAVED CHANGES
            ================================================= */}

            <Dialog
                open={showDiscardDialog}
                onClose={() =>
                    setShowDiscardDialog(false)
                }
            >
                <DialogTitle>
                    Unsaved Changes
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        You have unsaved changes.
                        Switching will discard them.
                        Continue?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            setShowDiscardDialog(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={() => {
                            if (
                                pendingSelection?.type ===
                                "term"
                            ) {
                                setIsDirty(false);

                                clearCurrentAssessment();

                                setTermNumber(
                                    pendingSelection.value
                                );

                                setPendingSelection(null);

                                setShowDiscardDialog(false);

                                return;
                            }

                            confirmDiscard();
                        }}
                        color="error"
                    >
                        Discard
                    </Button>
                </DialogActions>
            </Dialog>


            {/* =================================================
                MISSING SUBJECT / UNIT
            ================================================= */}

            <Dialog
                open={
                    showMissingSelectionDialog
                }
                onClose={() =>
                    setShowMissingSelectionDialog(
                        false
                    )
                }
            >
                <DialogTitle>
                    Missing Selection
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Please select both a subject
                        and unit before switching
                        students.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            setShowMissingSelectionDialog(
                                false
                            )
                        }
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Snackbar */}

            {SnackbarComponent && (
                <SnackbarComponent />
            )}


            {/* =================================================
                SAVE LOADING
            ================================================= */}

            {saveLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor:
                            "rgba(255,255,255,0.9)",
                        backdropFilter:
                            "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: {
                                xs: 3,
                                sm: 4,
                            },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection:
                                "column",
                            alignItems:
                                "center",
                            backgroundColor:
                                "white",
                            maxWidth: 400,
                            mx: 2,
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Saving student assessment..."
                        />
                    </Paper>
                </Box>
            )}
        </Box>
    );
};


export default UnitLevelProgress;
